import React from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';
import { InfoHint } from '@ooh/ui/components/info-hint';
import type { RoleScopeRow, ScopeOwnership, OfferDepartment } from '@ooh/contracts';
import {
  groupGrantsByScope,
  canAddScopeGroup,
  permissionDisplayName,
  type TranslateFn,
} from './grant-helpers';
import { ScopeGroupRow, type MediaTypeOption } from './scope-group-row';

// Re-exported so existing consumers (`permission-grant-list.tsx`,
// `use-role-catalog.ts`) can keep importing `MediaTypeOption` from here — the
// type's canonical home is now `./scope-group-row`, its lowest-level consumer.
export type { MediaTypeOption };

export interface GrantRowProps {
  permissionCode: string;
  permissionName: string;
  grants: RoleScopeRow[];
  disabled: boolean;
  /**
   * Whether the department-scope chip is interactive for this permission.
   * The server only enforces `department` for `offer.view_all`/`offer.delete`
   * (see `allowDepartmentScope` in `./role-codes`) — for every other
   * permission the chip renders read-only, forced to the wildcard label.
   */
  departmentScopeEditable: boolean;
  /** Full media-type catalog (data-driven — passed by the caller, not imported here). */
  mediaTypes: MediaTypeOption[];
  /** Whether the API evaluates media-type scope for this permission. */
  mediaTypeScopeEditable: boolean;
  /** `groupIndex` addresses one of this permission's independent scope groups — see `groupGrantsByScope`. */
  onChangeMediaTypes: (groupIndex: number, mediaTypeCodes: string[] | null) => void;
  onChangeOwnership: (groupIndex: number, ownership: ScopeOwnership | null) => void;
  onChangeDepartment: (groupIndex: number, department: OfferDepartment | null) => void;
  /** Adds a scope group — the initial "Nadaj" grant when ungranted, "Dodaj zakres" once already granted. */
  onAddScope: () => void;
  /** Removes one scope group; removing the last one revokes the permission entirely. */
  onRemoveScope: (groupIndex: number) => void;
  /**
   * Codes of other `PERMISSION_BUNDLES` members that are currently also
   * granted on this role (empty when this permission isn't bundled, or its
   * partner isn't granted). Non-empty renders the "required together with X"
   * inline notice explaining why the paired permission is granted — see
   * `bundlePartnersOf` in `../lib/grant-helpers` for how the caller computes
   * this from the role's full grant set.
   */
  pairedWithCodes: string[];
}

/**
 * Translated permission name plus an info-hint (i) affordance describing what
 * the permission grants. Both the display name and the description are routed
 * through i18n, keyed (nested) by the permission code, falling back to the raw
 * backend `name` when no translation exists yet.
 */
function PermissionNameLabel({
  permissionCode,
  permissionName,
  muted,
}: {
  permissionCode: string;
  permissionName: string;
  muted: boolean;
}) {
  const { t } = useTranslation('admin');
  const label = t(`permissionMatrix.permissions.${permissionCode}.name`, {
    defaultValue: permissionName,
  });
  const description = t(`permissionMatrix.permissions.${permissionCode}.desc`, {
    defaultValue: '',
  });

  return (
    <div className="flex items-center gap-1">
      <p className={`text-[13px] ${muted ? 'text-muted-foreground' : 'text-foreground'}`}>
        {label}
      </p>
      {description ? (
        <InfoHint label={t('permissionMatrix.permissionInfoLabel')} side="right" iconSize={13}>
          {description}
        </InfoHint>
      ) : null}
    </div>
  );
}

/**
 * "Required together with X" caption shown under a granted permission that
 * is currently paired with a bundle partner (see `pairedWithCodes` on
 * `GrantRowProps`). Renders nothing when there's no active pairing.
 */
function BundleNotice({ pairedWithCodes }: { pairedWithCodes: string[] }) {
  const { t } = useTranslation('admin');
  if (pairedWithCodes.length === 0) return null;

  const names = pairedWithCodes
    .map((code) => permissionDisplayName(t as TranslateFn, code))
    .join(', ');

  return (
    <p className="text-2xs text-muted-foreground/70 mt-0.5">
      {t('permissionMatrix.grant.bundleNotice', { names })}
    </p>
  );
}

/**
 * One permission inside a resource's grant card. The permission label
 * renders exactly once; below it, every independent scope group (see
 * `groupGrantsByScope`) renders as its own nested `ScopeGroupRow` line, each
 * with its own three scope-dimension chips (media type / ownership /
 * department) and its own remove affordance, plus a trailing "Dodaj zakres"
 * button to add another independent group. An ungranted permission (zero
 * groups) instead shows a quiet "grant" affordance.
 */
export function GrantRow({
  permissionCode,
  permissionName,
  grants,
  disabled,
  departmentScopeEditable,
  mediaTypes,
  mediaTypeScopeEditable,
  onChangeMediaTypes,
  onChangeOwnership,
  onChangeDepartment,
  onAddScope,
  onRemoveScope,
  pairedWithCodes,
}: GrantRowProps) {
  const { t } = useTranslation('admin');
  const groups = groupGrantsByScope(grants);
  const granted = groups.length > 0;
  // Once every (ownership, department) key is already used by an existing
  // group, adding another would collapse into (and silently widen) one of
  // them instead of creating a distinct group — disable the affordance
  // rather than let that happen. See `canAddScopeGroup`/`addScopeGroup`.
  const canAddScope = canAddScopeGroup(grants, departmentScopeEditable);

  if (!granted) {
    return (
      <div
        className="flex items-center justify-between py-2 first:pt-0"
        data-testid={`grant-row-${permissionCode}`}
      >
        <div className="min-w-0">
          <PermissionNameLabel
            permissionCode={permissionCode}
            permissionName={permissionName}
            muted
          />
          <p className="font-mono text-2xs text-muted-foreground/50">{permissionCode}</p>
        </div>
        {disabled ? (
          <span className="text-2xs text-muted-foreground/60">
            {t('permissionMatrix.grant.noAccess')}
          </span>
        ) : (
          <Button variant="ghost" size="xs" onClick={onAddScope} className="text-xs">
            <Plus className="w-3.5 h-3.5" />
            {t('permissionMatrix.grant.grantButton')}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div
      className="py-2 border-t border-border/30 first:border-t-0 first:pt-0"
      data-testid={`grant-row-${permissionCode}`}
    >
      <div className="min-w-0 pb-1">
        <PermissionNameLabel
          permissionCode={permissionCode}
          permissionName={permissionName}
          muted={false}
        />
        <p className="font-mono text-2xs text-muted-foreground/50">{permissionCode}</p>
        <BundleNotice pairedWithCodes={pairedWithCodes} />
      </div>
      {/* Nested scope groups — indented under the permission label above. */}
      <div className="pl-3 border-l border-border/30 space-y-0.5">
        {groups.map((groupGrants, groupIndex) => (
          <ScopeGroupRow
            // Index key: groups are re-derived from `grants` every render
            // (see `groupGrantsByScope`) and re-sort by first appearance, so
            // a stable identity beyond render order doesn't exist without
            // inventing one — acceptable for this compact editor list.
            // eslint-disable-next-line react/no-array-index-key
            key={groupIndex}
            grants={groupGrants}
            groupIndex={groupIndex}
            permissionGrants={grants}
            disabled={disabled}
            departmentScopeEditable={departmentScopeEditable}
            mediaTypeScopeEditable={mediaTypeScopeEditable}
            mediaTypes={mediaTypes}
            permissionName={permissionName}
            onChangeMediaTypes={(mts) => onChangeMediaTypes(groupIndex, mts)}
            onChangeOwnership={(o) => onChangeOwnership(groupIndex, o)}
            onChangeDepartment={(d) => onChangeDepartment(groupIndex, d)}
            onRemove={() => onRemoveScope(groupIndex)}
          />
        ))}
        {!disabled && (
          <Button
            variant="ghost"
            size="xs"
            onClick={onAddScope}
            disabled={!canAddScope}
            title={canAddScope ? undefined : t('permissionMatrix.grant.addScopeExhausted')}
            className="text-xs text-muted-foreground hover:text-foreground -ml-2"
            data-testid={`add-scope-${permissionCode}`}
          >
            <Plus className="w-3.5 h-3.5" />
            {t('permissionMatrix.grant.addScope')}
          </Button>
        )}
      </div>
    </div>
  );
}
